import React, { type ErrorInfo, type ReactNode } from 'react'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import { Button } from './Common/Button'
import { telemetryLogger } from '../services/telemetryLogger'

export interface TelemetryBoundaryProps {
  scope: 'root' | 'route'
  routeName?: string
  children: ReactNode
  fallback?: ReactNode
}

interface TelemetryBoundaryState {
  hasError: boolean
  error?: Error
}

export class TelemetryBoundary extends React.Component<
  TelemetryBoundaryProps,
  TelemetryBoundaryState
> {
  state: TelemetryBoundaryState = { hasError: false }

  static getDerivedStateFromError(error: Error): TelemetryBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const event = this.props.scope === 'root' ? 'ui.boundary.root' : 'ui.boundary.route'
    telemetryLogger.error(
      event,
      {
        routeName: this.props.routeName ?? '',
        componentStack: (info.componentStack ?? '').slice(0, 2000),
      },
      error
    )
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }
      return (
        <div
          role="alert"
          style={{
            padding: 32,
            textAlign: 'center',
            background: 'var(--surface-card)',
            color: 'var(--text)',
            borderRadius: 'var(--radius-card, 12px)',
            margin: 16,
            border: '1px solid var(--border)',
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px 0' }}>Something went wrong</h2>
          <p style={{ color: 'var(--muted)', fontSize: 14, margin: '0 0 20px 0' }}>
            {this.props.scope === 'root'
              ? 'An unexpected error occurred in the application.'
              : `An error occurred in this section (${this.props.routeName ?? 'route'}).`}
          </p>
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshRoundedIcon fontSize="small" />}
            onClick={this.handleReset}
          >
            Reload this section
          </Button>
        </div>
      )
    }
    return this.props.children
  }
}
